import { baseUrl } from "config";
import { isDefined, isEmpty, stripTags, truncate } from "lib/util";
import slugify from "slugify";
import { isAccomodationData, isNatureData, isTourData } from "./predicates";
import { getActivityForCategoryId } from "./util";

export const mapActivityToFactory = {
  "familie-kinder": createPOIFromData,
  touren: createTourFromData,
  unterkunft: createAccomodationFromData,
  "sport-gesundheit": createPOIFromData,
  sehenswert: createPOIFromData,
  naturatlas: createPOIFromData,
};

export const mapActivityToFactorySections = {
  touren: createTourSectionsFromData,
  sehenswert: createPOISectionsFromData,
  "sport-gesundheit": createPOISectionsFromData,
  unterkunft: createAccomodationSectionsFromData,
  naturatlas: createPOISectionsFromData,
  "familie-kinder": createPOISectionsFromData,
};

function getPrimaryImage(data: Travelguide.Outdooractive.POIData): {
  primaryImage?: Travelguide.Image;
} {
  const primaryImage = data.primaryImage || data.images.image[0];
  const { id, title } = primaryImage || {};
  return id
    ? {
        primaryImage: { id, title },
      }
    : {};
}

function getGeometry(
  data: Travelguide.Outdooractive.TourData
): Pick<Travelguide.Tour, "geometry" | "startingPoint">;
function getGeometry(
  data: Travelguide.Outdooractive.POIData
): Pick<Travelguide.POI, "geometry">;
function getGeometry(
  data: Travelguide.Outdooractive.POIData | Travelguide.Outdooractive.TourData
):
  | Pick<Travelguide.POI, "geometry">
  | Pick<Travelguide.Tour, "geometry" | "startingPoint"> {
  const points = data.geometry.split(" ");
  const geometry = points.map(createGeoCoordinate);

  if ("startingPoint" in data) {
    const { lon, lat } = data.startingPoint;
    return {
      geometry,
      startingPoint: { lng: lon, lat },
    } as Pick<Travelguide.Tour, "geometry" | "startingPoint">;
  }

  return {
    geometry: geometry.shift(),
  } as Pick<Travelguide.POI, "geometry">;
}

function getProtectedArea(
  data: Travelguide.Outdooractive.POIData
): Travelguide.ID | undefined {
  const region = data.regions.region.find(
    ({ type }) => type === "protectedarea"
  );
  return region ? region.id : undefined;
}

function getSectionDescription(
  data: Travelguide.Outdooractive.POIData
): Travelguide.POISection.Description {
  return {
    type: "description",
    value: data.longText,
  };
}

function getSectionImage(
  data: Travelguide.Outdooractive.POIData
): Travelguide.POISection.Image | null {
  const images = (data.images?.image || [])
    .filter((image) => image.gallery)
    .map(
      ({ id, title, author, source, license, meta }): Travelguide.Image => ({
        id,
        title,
        author: (author || meta?.author)?.replaceAll("©", "").trim(),
        source: source || meta?.source.name,
        license: license?.short || meta?.license?.short,
      })
    );

  if (!images.length) {
    return null;
  }
  return {
    type: "image",
    children: images,
  };
}

function getSectionDetail(
  data: Travelguide.Outdooractive.POIData
): Travelguide.POISection.Detail | null {
  const { phone, email, homepage, address } = data;
  const { street, zipcode, town } = address || {};
  if ([phone, email, homepage, street, zipcode, town].every(isEmpty)) {
    return null;
  }
  return {
    type: "detail",
    aside: true,
    value: {
      phone,
      email,
      homepage,
      address: {
        street,
        zipcode,
        town,
      },
    },
  };
}

function getSectionAccomodationDetail(
  data: Travelguide.Outdooractive.AccomodationData
): Travelguide.AccomodationSection.Detail | null {
  const poiSection = getSectionDetail(data);
  const { priceText } = data.lodging || {};
  if ([poiSection, priceText].every(isEmpty)) {
    return null;
  }
  return {
    type: "detail",
    aside: true,
    ...(poiSection || {}),
    value: {
      ...(poiSection?.value || {}),
      priceText,
    },
  };
}

function getSectionTourDetail(
  data: Travelguide.Outdooractive.TourData
): Travelguide.TourSection.Detail {
  return {
    type: "detail",
    aside: true,
    start: data.startingPointDescr,
    end: data.destination,
    loopTour: Boolean(
      data.properties?.property.some(({ tag }) => tag === "loopTour")
    ),
  };
}

function getSectionMap(
  data: Travelguide.Outdooractive.POIData
): Travelguide.POISection.Map {
  return {
    type: "map",
    elpro: isTourData(data),
    locationText: isAccomodationData(data) ? data.lodging?.location : undefined,
  };
}

function getSectionFeatures(
  data: Travelguide.Outdooractive.AccomodationData
): Travelguide.AccomodationSection.Feature | null {
  const value = data.lodging.classification?.map(({ title, properties }) => ({
    title,
    features: properties.property.map(({ text }) => text),
  }));
  if (!Array.isArray(value)) {
    return null;
  }
  return {
    type: "feature",
    value,
  };
}

function getProperties(
  data: Travelguide.Outdooractive.POIData
): Array<string> | undefined {
  if (!data.properties) {
    return;
  }
  const exclude = isAccomodationData(data)
    ? data.lodging.classification?.flatMap(({ properties: { property } }) =>
        property.map(({ tag }) => tag)
      )
    : undefined;

  return (
    exclude
      ? data.properties.property.filter(({ tag }) => !exclude.includes(tag))
      : data.properties.property
  ).map(({ text }) => text);
}

function getSectionInformation(
  data: Travelguide.Outdooractive.POIData
): Travelguide.POISection.Information | null;
function getSectionInformation(
  data: Travelguide.Outdooractive.TourData
): Travelguide.TourSection.Information | null;
function getSectionInformation(
  data: Travelguide.Outdooractive.POIData | Travelguide.Outdooractive.TourData
):
  | Travelguide.POISection.Information
  | Travelguide.TourSection.Information
  | null {
  const tourInfo = !isTourData(data)
    ? {
        businessHours: data.businessHours ? data.businessHours : undefined,
        pricing: data.fee,
      }
    : {};
  const properties = getProperties(data);
  const protectedArea =
    isTourData(data) || isNatureData(data)
      ? { protectedAreaId: getProtectedArea(data) }
      : {};

  if (
    [Object.keys(tourInfo), properties, Object.keys(protectedArea)].every(
      isEmpty
    )
  ) {
    return null;
  }

  return {
    type: "information",
    ...tourInfo,
    properties,
    ...protectedArea,
  };
}

function getSectionDirections(
  data: Travelguide.Outdooractive.POIData
): Travelguide.POISection.Directions {
  return {
    type: "directions",
    value: {
      publicTransit: data.publicTransit,
      gettingThere: data.gettingThere,
      parking: data.parking,
    },
  };
}

export function getCategory(data: Travelguide.Outdooractive.POIData): {
  category: Travelguide.Category;
} {
  return {
    category: {
      id: data.category.id,
      name: data.category.name,
      path: data.category.path,
      activity: data.category.path
        ?.map(getActivityForCategoryId)
        .find((activity) => activity != undefined),
    },
  };
}

export function findCategoryIdPath(
  tree: Travelguide.Outdooractive.CategoryData[],
  targetId: string,
  path: string[] = []
): string[] {
  for (const node of tree) {
    const currentPath = path.concat(node.id);

    if (node.id === targetId) {
      return currentPath;
    }

    if (node.category && node.category.length > 0) {
      const foundPath = findCategoryIdPath(
        node.category,
        targetId,
        currentPath
      );

      if (foundPath.length > 0) {
        return foundPath;
      }
    }
  }
  return [];
}

export function enhanceWithCategoryPath(
  tree: Array<Travelguide.Outdooractive.CategoryData>
) {
  return <T extends Travelguide.Outdooractive.POIData>(ooi: T): T => ({
    ...ooi,
    category: {
      ...ooi.category,
      path: findCategoryIdPath(tree, ooi.category.id),
    },
  });
}

export function bakeImage(
  image: Travelguide.Image,
  {
    width,
    height,
    extension = "jpg",
  }: {
    width: number;
    height: number;
    extension?: "jpg" | "png";
  }
): Travelguide.BakedImage {
  const { id, title } = image;
  const slug = truncate(
    title ? slugify(title, { remove: /[^\d\w-\s]/, lower: true }) : id,
    64
  );
  const src = `${baseUrl}${id}/${width}x${height}c/${slug}.${extension}`;
  return {
    ...image,
    src,
    alt: title || "",
  };
}

export function createProtectedArea(data: {
  region: Array<Travelguide.Outdooractive.POIData>;
}): Array<Travelguide.POI> {
  return data.region
    .filter(
      (region): region is Travelguide.Outdooractive.ProtectedAreaData =>
        "protectedArea" in region
    )
    .map((region) => ({
      ...createPOIFromData(region),
      properties: region.protectedArea.protectedAreaRules.property.map(
        ({ tag, text }) => ({ tag, text })
      ),
    }));
}

function createGeoCoordinate(
  value: string
): Travelguide.GeoCoordinate2D | Travelguide.GeoCoordinate3D {
  const [lng, lat, alt] = value.split(",").map(parseFloat);
  return {
    lng,
    lat,
    alt,
  };
}

export function createPOIFromData(
  data: Travelguide.Outdooractive.POIData
): Travelguide.POI {
  const { id, title, shortText } = data;
  return {
    id,
    title,
    slugTitle: truncate(
      slugify(title, { remove: /[^\d\w-\s]/, lower: true }),
      64
    ),
    shortText: stripTags(shortText),
    ...getCategory(data),
    ...getGeometry(data),
    ...getPrimaryImage(data),
  };
}

export function createPOISectionsFromData(
  data: Travelguide.Outdooractive.POIData
): Array<Travelguide.POISection> {
  return [
    getSectionDescription(data),
    getSectionDetail(data),
    getSectionImage(data),
    getSectionInformation(data),
    getSectionMap(data),
    getSectionDirections(data),
  ].filter(isDefined);
}

export function createTourFromData(
  data: Travelguide.Outdooractive.TourData
): Travelguide.Tour {
  return {
    ...createPOIFromData(data),
    ...getGeometry(data),
    rating: {
      condition: data.rating.condition,
      difficulty: data.rating.difficulty,
      qualityOfExperience: data.rating.qualityOfExperience,
    },
    duration: data.time.min,
    elevation: {
      ascent: data.elevation.ascent,
      descent: data.elevation.descent,
    },
    distance: data.length,
  };
}

export function createTourSectionsFromData(
  data: Travelguide.Outdooractive.TourData
): Array<Travelguide.TourSection> {
  return [
    getSectionDescription(data),
    getSectionTourDetail(data),
    getSectionMap(data),
    getSectionImage(data),
    getSectionInformation(data),
    getSectionDirections(data),
  ].filter(isDefined);
}

export function createAccomodationFromData(
  data: Travelguide.Outdooractive.AccomodationData
): Travelguide.Accomodation {
  return {
    ...createPOIFromData(data),
    ...getCategory(data),
    price: data.lodging.priceText
      ? data.lodging.priceText.replaceAll(/\s+/g, " ")
      : "",
  };
}

export function createAccomodationSectionsFromData(
  data: Travelguide.Outdooractive.AccomodationData
): Array<Travelguide.AccomodationSection> {
  return [
    getSectionDescription(data),
    getSectionAccomodationDetail(data),
    getSectionImage(data),
    getSectionInformation(data),
    getSectionMap(data),
    getSectionFeatures(data),
    getSectionDirections(data),
  ].filter(isDefined);
}
